import { createRouter, createWebHistory } from 'vue-router';
import AdminLayout from '@/layouts/AdminLayout.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import HomePage from '@/views/HomePage.vue';
import LandingPage from '@/components/LandingPage.vue';
import Login from '@/components/Login.vue';
import Dashboard from '@/components/Dashboard.vue';
import AdvertisementList from '@/components/AdvertisementList.vue';
import AdvertisementForm from '@/components/AdvertisementForm.vue';
import CategoryList from '../components/CategoryList.vue';
import CouponList from '../components/CouponList.vue';
import CategoryForm from '../components/CategoryForm.vue';
import CouponForm from '../components/CouponForm.vue';
import RegisterAdvertisement from '@/components/RegisterAdvertisement.vue';
import ForgotPassword from '@/components/ForgotPassword.vue';
import RegisterUser from '@/components/RegisterUser.vue';
import OtpValidation from '@/components/OtpValidation.vue';
import Result from '@/components/Result.vue';
import Confirmation from '@/components/Confirmation.vue';
import ClientList from '@/components/ClientList.vue';
import ClientForm from '@/components/ClientForm.vue';
import UserList from '@/components/UserList.vue';
import UserForm from '@/components/UserForm.vue';
import EnterpriseList from '@/components/EnterpriseList.vue';
import EnterpriseForm from '@/components/EnterpriseForm.vue';
import EditAdvertisementForm from '@/components/EditAdvertisementForm.vue';
import RenewalAdvertisementForm from '@/components/RenewalAdvertisementForm.vue';
import AdvertisementDetails from '@/components/AdvertisementDetails.vue';
import SubscriptionsList from '@/components/SubscriptionsList.vue';
import SubscriptionForm from '@/components/SubscriptionForm.vue';
import ContactForm from '@/components/ContactForm.vue';

const PUBLIC_ROUTES = [
  'login',
  'forgot-password',
  'register',
  'otp-validation',
  'HomePage',
  'LandingPage', // Nova página de entrada
];

const routes = [
  {
    path: '/login',
    name: 'login',
    component: Login,
  },
  {
    path: '/esqueceu-senha',
    name: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/cadastro',
    name: 'register',
    component: RegisterUser,
  },
  {
    path: '/validar-codigo',
    name: 'otp-validation',
    component: OtpValidation,
  },
  {
    path: '/confirmacao/:reference/:hash',
    name: 'confirmation',
    component: Confirmation,
  },
  {
    path: '/',
    name: 'LandingPage',
    component: LandingPage,
  },
  {
    path: '/app-home',
    component: DefaultLayout,
    children: [
      {
        path: '/app-home',
        name: 'HomePage',
        component: HomePage,
      },
      {
        path: "/contato",
        name: "Contato",
        component: ContactForm,
      },
      {
        path: '/anuncio/:slug',
        name: 'detalhes',
        component: AdvertisementDetails,
        props: true
      },
      {
        path: '/cadastre-seu-anuncio',
        name: 'register-advertisement',
        component: RegisterAdvertisement,
        meta: { requiresAuth: true },
      },
      {
        path: '/edite-seu-anuncio/:id',
        name: 'edit-advertisement',
        component: EditAdvertisementForm,
        meta: { requiresAuth: true },
      },
      {
        path: '/renove-seu-anuncio',
        name: 'renewal-advertisement',
        component: RenewalAdvertisementForm,
        meta: { requiresAuth: true },
      },
      {
        path: '/payment/result',
        name: 'PaymentResult',
        component: Result,
      },
    ],
  },
  {
    path: '/app',
    component: AdminLayout,
    meta: { requiresAuth: true, adminOnly: true }, // 🔒 exige autenticação e role admin/administrative
    children: [
      { path: 'dashboard', name: 'Dashboard', component: Dashboard },
      { path: 'anuncios', name: 'Anuncios', component: AdvertisementList },
      { path: 'anuncios/novo', name: 'advertisement-create', component: AdvertisementForm },
      { path: 'anuncios/editar/:id', name: 'advertisement-edit', component: AdvertisementForm, props: true },
      { path: 'categorias', name: 'Categorias', component: CategoryList },
      { path: 'categorias/novo', name: 'category-create', component: CategoryForm },
      { path: 'categorias/editar/:id', name: 'category-edit', component: CategoryForm, props: true },
      { path: 'cupons', name: 'Cupons', component: CouponList },
      { path: 'cupons/novo', name: 'coupon-create', component: CouponForm },
      { path: 'cupons/editar/:id', name: 'coupon-edit', component: CouponForm, props: true },
      { path: 'usuarios', name: 'Usuários', component: UserList },
      { path: 'usuarios/novo', name: 'user-create', component: UserForm },
      { path: 'usuarios/editar/:id', name: 'user-edit', component: UserForm, props: true },
      { path: 'empresa', name: 'Empresa', component: EnterpriseList },
      { path: 'empresa/editar/:id', name: 'enterprise-edit', component: EnterpriseForm, props: true },
      { path: 'clientes', name: 'Clientes', component: ClientList },
      { path: 'clientes/novo', name: 'client-create', component: ClientForm },
      { path: 'clientes/editar/:id', name: 'client-edit', component: ClientForm, props: true },
      { path: 'assinaturas', name: 'Assinaturas', component: SubscriptionsList },
      { path: 'assinaturas/novo', name: 'subscription-create', component: SubscriptionForm },
      { path: 'assinaturas/editar/:id', name: 'subscription-edit', component: SubscriptionForm, props: true },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const isAuthenticated = !!localStorage.getItem('userData');
  const user = JSON.parse(localStorage.getItem('userData') || '{}');
  const role = user?.user?.role;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const adminOnly = to.matched.some((record) => record.meta.adminOnly);
  const routeName = to.name || '';

  // 🔒 Rota exige login
  if (requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } });
  }

  // 🚫 Bloqueia acesso a /app se não for admin/administrative
  if (adminOnly && !['admin', 'administrative'].includes(role)) {
    return next({ name: 'HomePage' });
  }

  // 🏠 Redireciona usuários autenticados da LandingPage para HomePage
  if (isAuthenticated && routeName === 'LandingPage') {
    if (['admin', 'administrative'].includes(role)) {
      return next({ name: 'Dashboard' });
    } else {
      return next({ name: 'HomePage' });
    }
  }

  /*// 🔁 Evita que usuários autenticados acessem rotas públicas (login, cadastro, etc.)
  if (isAuthenticated && PUBLIC_ROUTES.includes(routeName)) {
    if (role === 'client') {
      return next({ name: 'HomePage' });
    } else if (['admin', 'administrative'].includes(role)) {
      return next({ name: 'Dashboard' });
    }
  }*/

  next();
});

export default router;
